import {getMedicationPrimaryDoseLabel} from '../medications/medicationDosePresentation';
import {formatMedicationDose,readMedicationDose} from '../../shared/medicationDose';
import React from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddRounded as AddRoundedIcon,
  NotificationsNoneRounded as NotificationsNoneRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';

const normalizeWhitespace = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const titleCaseFirst = (value = '') => {
  const text = normalizeWhitespace(value);
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : '';
};

const formatDateKeyLabel = (value = '') => {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
    : value;
};

const dayBefore = (value = '') => {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
};

const getPrimarySchedule = (medication = {}) => {
  if (!medication) return {};
  const schedules = Array.isArray(medication.schedules) ? medication.schedules : [];
  return schedules[0] || {};
};

const formatDose=getMedicationPrimaryDoseLabel;

const formatSchedule = (medication = {}) => (
  medication ? normalizeWhitespace(
    medication.sourceScheduleText
    || medication.schedule
    || getPrimarySchedule(medication).time
    || medication.time
    || ''
  ) : ''
);

const formatDisplaySchedule = (schedule = '', status = '') => {
  const normalizedSchedule = normalizeWhitespace(schedule);
  const isAsNeeded = /^as[ -]?needed$/i.test(normalizeWhitespace(status));
  if (!isAsNeeded || /^as needed\b/i.test(normalizedSchedule)) return normalizedSchedule;
  return normalizeWhitespace(`As needed ${normalizedSchedule}`);
};

const formatDisplayForm = (form = '') => {
  const normalizedForm = normalizeWhitespace(form);
  if (!normalizedForm) return '';
  return `${normalizedForm.charAt(0).toUpperCase()}${normalizedForm.slice(1)}`;
};

const getCandidateDateLabel = (candidate = {}) => (
  normalizeWhitespace(
    candidate.suggestion?.changeDateText
    || candidate.planHistoryEntries?.find((entry) => entry?.type === 'dose_change')?.approximateDateText
    || candidate.suggestion?.startDateText
    || candidate.planHistoryEntries?.[0]?.approximateDateText
    || ''
  )
);

const getCandidateReason = (candidate = {}) => (
  normalizeWhitespace(
    candidate.suggestion?.changeReason
    || candidate.planHistoryEntries?.find((entry) => entry?.reason)?.reason
    || ''
  )
);

const hasExactDateReview = (candidate = {}) => {
  const historyEntries = Array.isArray(candidate.planHistoryEntries) ? candidate.planHistoryEntries : [];
  return historyEntries.some((entry) => (
    entry?.dateApproximate
    || (Array.isArray(entry?.reviewReasons) && entry.reviewReasons.includes('exact_date_needed'))
  ));
};

const formatPlanText = (medication = {}) => (
  [formatDose(medication), formatSchedule(medication)].filter(Boolean).join(' • ')
);

const buildMedicationDraft = (candidate = {}) => {
  const suggestion = candidate.suggestion || {};
  const nextMedication = candidate.nextMedication || {};
  const schedule = getPrimarySchedule(nextMedication);

  return {
    name: normalizeWhitespace(nextMedication.name || suggestion.name || ''),
    dose: normalizeWhitespace(schedule.dose || nextMedication.dose || suggestion.dose || ''),
    unit: normalizeWhitespace(schedule.unit || nextMedication.unit || suggestion.unit || ''),
    schedule: normalizeWhitespace(nextMedication.sourceScheduleText || nextMedication.schedule || suggestion.schedule || schedule.time || nextMedication.time || ''),
    form: normalizeWhitespace(nextMedication.form || suggestion.form || ''),
    status: normalizeWhitespace(nextMedication.sourceStatus || nextMedication.status || suggestion.status || 'Active'),
    notes: normalizeWhitespace(nextMedication.notes || suggestion.notes || suggestion.purpose || ''),
    holdStartDate: suggestion.temporaryHold?.startDate || nextMedication.temporaryHold?.startDate || '',
    resumeDate: suggestion.temporaryHold?.resumeDate || nextMedication.temporaryHold?.resumeDate || '',
    holdReason: suggestion.temporaryHold?.reason || nextMedication.temporaryHold?.reason || '',
  };
};

const buildCandidateWithDraft = (candidate = {}, draft = {}) => {
  const nextMedication = candidate.nextMedication || {};
  const schedules = Array.isArray(nextMedication.schedules) ? nextMedication.schedules : [];
  const firstSchedule = schedules[0] || {};
  const dose = normalizeWhitespace(draft.dose);
  const unit = normalizeWhitespace(draft.unit);
  const scheduleText = normalizeWhitespace(draft.schedule);
  const nextSchedules = [{
    ...firstSchedule,
    dose,
    unit,
    time: scheduleText,
  }];

  return {
    ...candidate,
    nextMedication: {
      ...nextMedication,
      name: normalizeWhitespace(draft.name),
      dose,
      unit,
      schedule: scheduleText,
      sourceScheduleText: scheduleText,
      time: scheduleText,
      form: normalizeWhitespace(draft.form),
      status: normalizeWhitespace(draft.status),
      sourceStatus: normalizeWhitespace(draft.status),
      notes: normalizeWhitespace(draft.notes),
      schedules: nextSchedules,
      temporaryHold: candidate.suggestion?.planAction === 'temporary_hold' ? {
        startDate: draft.holdStartDate,
        endDate: dayBefore(draft.resumeDate),
        resumeDate: draft.resumeDate,
        reason: normalizeWhitespace(draft.holdReason),
      } : nextMedication.temporaryHold || null,
    },
  };
};

const formatChangeText = (before = '', after = '') => {
  if (before && after) return `${before} -> ${after}`;
  return after || before || '';
};

const getStatusTone = (status = '') => {
  const normalized = normalizeWhitespace(status).toLowerCase();
  const stopped = /stopped|paused|inactive|discontinued/.test(normalized);
  if (stopped) {
    return {
      bg: colors.semantic.profileFlow.notStartedBg,
      ink: colors.semantic.ink2,
      dot: colors.semantic.neutral,
    };
  }

  return {
    bg: colors.semantic.profileFlow.completionBg,
    ink: colors.semantic.profileFlow.completionText,
    dot: colors.semantic.sageDeep,
  };
};

export const canOfferMedicationReminder = (medication = {}) => {
  if (!medication?.id || medication.isArchived) return false;
  const status = normalizeWhitespace(
    medication.sourceStatus || medication.status || medication.medicationStatus
  ).toLowerCase();
  const scheduleType = normalizeWhitespace(medication.scheduleType).toLowerCase();
  const schedule = formatSchedule(medication).toLowerCase();

  if (/stopped|paused|inactive|discontinued|completed/.test(status)) return false;
  if (scheduleType === 'prn' || scheduleType === 'as_needed') return false;
  return !/^as[ -]?needed\b/.test(status) && !/^as[ -]?needed\b/.test(schedule);
};

const DetailPill = ({ label, tone = 'neutral', withStatusDot = false }) => {
  if (!label) return null;

  const toneMap = {
    green: {
      bg: colors.semantic.profileFlow.completionBg,
      ink: colors.semantic.profileFlow.completionText,
      dot: colors.semantic.sageDeep,
    },
    neutral: {
      bg: colors.semantic.profileFlow.notStartedBg,
      ink: colors.semantic.ink,
      dot: colors.semantic.ink2,
    },
  };
  const resolvedTone = typeof tone === 'string' ? toneMap[tone] : tone;

  return (
    <Box
      component="span"
      sx={{
        minHeight: 26,
        px: 0.8,
        py: 0.28,
        borderRadius: '999px',
        bgcolor: resolvedTone.bg,
        color: resolvedTone.ink,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.45,
        fontSize: '0.76rem',
        fontWeight: 850,
        lineHeight: 1.15,
      }}
    >
      {withStatusDot ? (
        <Box
          component="span"
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: resolvedTone.dot,
            flexShrink: 0,
          }}
        />
      ) : null}
      {label}
    </Box>
  );
};

const ChangeDetail = ({ label, value, component = 'div' }) => {
  if (!value) return null;

  return (
    <Box
      component={component}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '72px minmax(0, 1fr)', sm: '100px minmax(0, 1fr)' },
        columnGap: 1,
        alignItems: 'baseline',
        minWidth: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.74rem',
          fontWeight: 850,
          color: alpha(colors.semantic.ink, 0.6),
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          minWidth: 0,
          fontSize: '0.78rem',
          fontWeight: 650,
          color: colors.semantic.ink,
          lineHeight: 1.35,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const MedicationPlanCandidateReviewRow = ({
  candidate,
  sourceTitle = 'Medication source',
  isResolved = false,
  isIgnored = false,
  isEditing = false,
  isSaving = false,
  decisionLabel = 'Needs decision',
  onSave,
  onEditFirst,
  onIgnore,
  onSetReminder,
  resolvedMedication = null,
  reminderState = null,
  reminderPath = '',
  pageLayout = false,
}) => {
  const [draft, setDraft] = React.useState(() => buildMedicationDraft(candidate));
  const suggestion = candidate.suggestion || {};
  const nextMedication = candidate.nextMedication || {};
  React.useEffect(() => {
    setDraft(buildMedicationDraft(candidate));
  }, [candidate]);

  const isUpdate = candidate.suggestedAction === 'update_existing';
  const isTemporaryHold = candidate.suggestedAction === 'temporary_hold'
    || suggestion.planAction === 'temporary_hold';
  const isCourseCompletion = candidate.suggestedAction === 'complete_course'
    || suggestion.planAction === 'complete_course';
  const needsMedicationMatch = candidate.suggestedAction === 'match_required';
  const showEditFields = isEditing && !isResolved && !isIgnored;
  const hold = showEditFields ? {
    startDate: draft.holdStartDate,
    resumeDate: draft.resumeDate,
    reason: draft.holdReason,
  } : suggestion.temporaryHold || nextMedication.temporaryHold || {};
  const completedCourse = suggestion.completedCourse || nextMedication.completedCourse || {};
  const isNoChange = candidate.suggestedAction === 'no_change';
  const isMatched = Boolean(candidate.matchedMedication);
  const existingDose = formatDose(candidate.matchedMedication);
  const extractedDose = showEditFields
    ? formatMedicationDose({dose:draft.dose,unit:draft.unit})
    : formatDose(nextMedication);
  const existingSchedule = formatSchedule(candidate.matchedMedication);
  const extractedSchedule = showEditFields ? draft.schedule : formatSchedule(nextMedication);
  const dateLabel = getCandidateDateLabel(candidate);
  const reason = getCandidateReason(candidate);
  const notes = showEditFields ? draft.notes : normalizeWhitespace(nextMedication.notes || suggestion.notes || suggestion.purpose || '');
  const status = showEditFields ? draft.status : normalizeWhitespace(suggestion.status || nextMedication.sourceStatus || nextMedication.status || 'Active');
  const form = showEditFields ? draft.form : normalizeWhitespace(suggestion.form || nextMedication.form || '');
  const displaySchedule = formatDisplaySchedule(extractedSchedule, status);
  const displayForm = formatDisplayForm(form);
  const reviewText = [
    needsMedicationMatch ? 'medication is not in the current list' : '',
    !isTemporaryHold && !normalizeWhitespace(showEditFields ? draft.dose : suggestion.dose || '') ? 'dose needed' : '',
    hasExactDateReview(candidate) ? 'exact date needed' : '',
  ].filter(Boolean).join(', ');
  const normalizedDose = normalizeWhitespace(
    showEditFields
      ? draft.dose
      : getPrimarySchedule(nextMedication).dose || nextMedication.dose || suggestion.dose || ''
  );
  const blocksDirectSave = isTemporaryHold
    ? !candidate.matchedMedication || !hold.startDate || !hold.resumeDate
    : !readMedicationDose({dose:normalizedDose,unit:showEditFields?draft.unit:(getPrimarySchedule(nextMedication).unit || nextMedication.unit || suggestion.unit)}).doseText;
  const blocksEditedSave = showEditFields && !normalizeWhitespace(draft.name);
  const statusTone = getStatusTone(status);
  const decisionTone = isResolved || isNoChange
    ? {
        bg: colors.semantic.profileFlow.completionBg,
        ink: colors.semantic.profileFlow.completionText,
        border: colors.semantic.profileFlow.progressPartial,
      }
    : isIgnored
      ? {
          bg: colors.semantic.profileFlow.notStartedBg,
          ink: alpha(colors.semantic.ink, 0.72),
          border: colors.semantic.profileFlow.cardBorder,
        }
      : isEditing
        ? {
            bg: alpha(colors.semantic.warning, 0.1),
            ink: colors.semantic.profileFlow.inProgressText,
            border: alpha(colors.semantic.warning, 0.28),
          }
        : {
            bg: colors.brand.ice,
            ink: colors.brand.deep,
            border: colors.brand.tint,
          };
  const bannerTone = isCourseCompletion
    ? {
        bg: colors.semantic.profileFlow.completionBg,
        border: colors.semantic.profileFlow.progressPartial,
        ink: colors.semantic.profileFlow.completionText,
        icon: <CheckRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Completed course',
      }
    : isMatched
    ? {
        bg: colors.semantic.profileFlow.completionBg,
        border: colors.semantic.profileFlow.progressPartial,
        ink: colors.semantic.profileFlow.completionText,
        icon: <CheckRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Already in your list',
      }
    : {
        bg: colors.brand.ice,
        border: colors.brand.tint,
        ink: colors.brand.deep,
        icon: <AddRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'New medication',
      };
  const saveLabel = isResolved
    ? 'Added'
    : isSaving
      ? 'Saving...'
      : isTemporaryHold
        ? needsMedicationMatch ? 'Medication match needed' : 'Apply temporary hold'
      : isCourseCompletion
        ? isMatched ? 'Complete course' : 'Save completion to Timeline'
      : isUpdate
        ? 'Update medication'
        : showEditFields
          ? 'Add medication'
          : 'Add medication';
  const pageSaveLabel = isResolved ? 'Applied' : isSaving ? 'Saving...' : isTemporaryHold ? needsMedicationMatch ? 'Medication match needed' : 'Apply temporary hold' : isCourseCompletion ? isMatched ? 'Complete course' : 'Save to Timeline' : isUpdate ? 'Update list' : 'Add to list';
  const isPrimaryActionDisabled = isResolved || isIgnored || isSaving || blocksDirectSave || blocksEditedSave;
  const actionMedicationName = showEditFields
    ? normalizeWhitespace(draft.name || suggestion.name)
    : normalizeWhitespace(suggestion.name || nextMedication.name);
  const handleDraftChange = (field) => (event) => {
    const { value } = event.target;
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };
  const handleSave = () => {
    onSave(buildCandidateWithDraft(candidate, draft));
  };

  return (
    <Box
      sx={{
        borderRadius: pageLayout ? 2.5 : 2,
        border: `1px solid ${pageLayout ? colors.semantic.profileFlow.cardBorder : bannerTone.border}`,
        bgcolor: colors.semantic.profileFlow.cardBg,
        overflow: 'hidden',
      }}
    >
      {!pageLayout ? <Box
        sx={{
          px: { xs: 1.05, sm: 1.25 },
          py: 0.75,
          bgcolor: bannerTone.bg,
          color: bannerTone.ink,
          display: 'flex',
          alignItems: 'center',
          gap: 0.55,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row" spacing={0.35} alignItems="center">
          {bannerTone.icon}
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, lineHeight: 1.2 }}>
            {bannerTone.label}
          </Typography>
        </Stack>
        {candidate.matchedMedication?.name ? (
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 750, color: alpha(colors.semantic.ink, 0.62), lineHeight: 1.2 }}>
            Matched: {candidate.matchedMedication.name}{isNoChange ? ' · No plan change found' : ''}
          </Typography>
        ) : null}
      </Box> : null}

      <Box sx={{ px: { xs: 1.05, sm: 1.25 }, py: 1 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={0.6}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          {showEditFields ? (
            <TextField
              label="Medication name"
              value={draft.name}
              onChange={handleDraftChange('name')}
              size="small"
              fullWidth
              autoFocus
              inputProps={{ 'aria-label': 'Medication name' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.4,
                  bgcolor: '#FFFFFF',
                },
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '0.98rem', fontWeight: 900, color: colors.landing.heroText, lineHeight: 1.15 }}>
              {suggestion.name || nextMedication.name || 'Medication'}
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: '999px',
              bgcolor: pageLayout ? 'transparent' : decisionTone.bg,
              color: pageLayout ? colors.semantic.ink2 : decisionTone.ink,
              border: pageLayout ? 'none' : `1px solid ${decisionTone.border}`,
              fontSize: '0.7rem',
              fontWeight: 900,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {pageLayout && !isResolved && !isIgnored ? (isCourseCompletion ? 'Completed course' : isMatched ? 'Current' : 'New') : decisionLabel}
          </Box>
        </Stack>
        {showEditFields ? (
          <Stack spacing={0.75} sx={{ mt: 0.85 }}>
            {isTemporaryHold ? (
              <>
                <TextField
                  label="Hold starts"
                  type="date"
                  value={draft.holdStartDate}
                  onChange={handleDraftChange('holdStartDate')}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Resume on"
                  type="date"
                  value={draft.resumeDate}
                  onChange={handleDraftChange('resumeDate')}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Reason"
                  value={draft.holdReason}
                  onChange={handleDraftChange('holdReason')}
                  size="small"
                />
              </>
            ) : <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.65}>
              <TextField
                label="Dose"
                value={draft.dose}
                onChange={handleDraftChange('dose')}
                size="small"
                inputProps={{ 'aria-label': 'Dose' }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Unit"
                value={draft.unit}
                onChange={handleDraftChange('unit')}
                size="small"
                inputProps={{ 'aria-label': 'Unit' }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <TextField
              label="Timing"
              value={draft.schedule}
              onChange={handleDraftChange('schedule')}
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Timing' }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.65}>
              <TextField
                label="Form"
                value={draft.form}
                onChange={handleDraftChange('form')}
                size="small"
                inputProps={{ 'aria-label': 'Form' }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Status"
                value={draft.status}
                onChange={handleDraftChange('status')}
                size="small"
                inputProps={{ 'aria-label': 'Status' }}
                sx={{ flex: 1 }}
              />
            </Stack>
            </>}
          </Stack>
        ) : (
          pageLayout ? (
            <Stack
              component="ul"
              aria-label="Medication details"
              spacing={0.55}
              sx={{ mt: 1, mb: 0, pl: 0, listStyle: 'none' }}
            >
              <ChangeDetail component="li" label="Dose" value={extractedDose || 'Not found'} />
              <ChangeDetail component="li" label="Schedule" value={displaySchedule || 'Not found'} />
              {displayForm ? <ChangeDetail component="li" label="Form" value={displayForm} /> : null}
              {isTemporaryHold ? <ChangeDetail component="li" label="Plan" value="Temporary hold" /> : null}
              {isTemporaryHold ? <ChangeDetail component="li" label="Hold starts" value={formatDateKeyLabel(hold.startDate)} /> : null}
              {isTemporaryHold ? <ChangeDetail component="li" label="Resume" value={formatDateKeyLabel(hold.resumeDate)} /> : null}
              {isTemporaryHold && hold.reason ? <ChangeDetail component="li" label="Reason" value={titleCaseFirst(hold.reason)} /> : null}
              {isCourseCompletion ? <ChangeDetail component="li" label="Course completed" value={formatDateKeyLabel(completedCourse.effectiveDate)} /> : null}
              {isCourseCompletion && completedCourse.reason ? <ChangeDetail component="li" label="Outcome" value={completedCourse.reason} /> : null}
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.55} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
              <DetailPill label={extractedDose || 'Dose not found'} tone={extractedDose ? 'green' : 'neutral'} />
              <DetailPill label={extractedSchedule || 'Timing not found'} tone="neutral" />
              {form ? <DetailPill label={form} tone="neutral" /> : null}
              <DetailPill label={status} tone={statusTone} withStatusDot />
            </Stack>
          )
        )}

        {showEditFields ? (
          <TextField
            label="Notes"
            value={draft.notes}
            onChange={handleDraftChange('notes')}
            size="small"
            fullWidth
            multiline
            minRows={3}
            inputProps={{ 'aria-label': 'Notes' }}
            sx={{ mt: 0.85 }}
          />
        ) : notes ? (
          <Box
            sx={{
              mt: 0.85,
              p: 0.9,
              borderRadius: 1.2,
              bgcolor: colors.semantic.profileFlow.notStartedBg,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.79rem',
                color: colors.semantic.ink,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {notes}
            </Typography>
          </Box>
        ) : null}

        {(isUpdate || dateLabel || reason || reviewText || (!pageLayout && sourceTitle)) ? (
          <>
            <Divider sx={{ my: 0.95, borderColor: alpha(colors.brand.tint, 0.22) }} />
            <Stack spacing={0.45}>
              {isUpdate ? (
                <>
                  <ChangeDetail label="Current plan" value={formatPlanText(candidate.matchedMedication)} />
                  <ChangeDetail label="After approval" value={formatPlanText(nextMedication)} />
                </>
              ) : null}
              {candidate.changes?.doseChanged ? (
                <ChangeDetail label="Dose change" value={formatChangeText(existingDose, extractedDose)} />
              ) : null}
              {candidate.changes?.scheduleChanged ? (
                <ChangeDetail label="Timing change" value={formatChangeText(existingSchedule || 'none', extractedSchedule)} />
              ) : null}
              {dateLabel ? (
                <ChangeDetail label="Change date" value={`${dateLabel}${hasExactDateReview(candidate) ? ' (approximate)' : ''}`} />
              ) : null}
              {reason ? <ChangeDetail label="Reason" value={reason} /> : null}
              {reviewText ? (
                <Stack
                  direction="row"
                  spacing={0.55}
                  alignItems="center"
                  sx={{
                    p: 0.75,
                    borderRadius: 1.2,
                    bgcolor: alpha(colors.semantic.warning, 0.1),
                    border: `1px solid ${alpha(colors.semantic.warning, 0.22)}`,
                  }}
                >
                  <WarningAmberRoundedIcon sx={{ fontSize: 16, color: colors.semantic.warning, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 850, color: colors.semantic.profileFlow.inProgressText }}>
                    Needs your review: {reviewText}
                  </Typography>
                </Stack>
              ) : null}
              {!pageLayout && sourceTitle ? (
                <Stack direction="row" spacing={0.35} alignItems="center" sx={{ minWidth: 0, pt: 0.15 }}>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 13, color: colors.semantic.ink2, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      minWidth: 0,
                      fontSize: '0.68rem',
                      color: colors.semantic.ink2,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {sourceTitle}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </>
        ) : null}
      </Box>

      <Box
        sx={{
          px: { xs: 1.05, sm: 1.25 },
          py: 0.85,
          borderTop: `1px solid ${alpha(colors.brand.tint, 0.2)}`,
        }}
      >
        {isResolved && reminderState?.configured ? (
          <Typography
            sx={{
              mb: 0.65,
              fontSize: '0.74rem',
              fontWeight: 850,
              color: reminderState.active
                ? colors.semantic.profileFlow.completionText
                : alpha(colors.semantic.ink, 0.62),
            }}
          >
            {reminderState.active ? '✓ ' : ''}{reminderState.label}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={0.6} alignItems="center">
          {isResolved && canOfferMedicationReminder(resolvedMedication) && (onSetReminder || reminderPath) ? (
            <Button
              variant={reminderState?.configured ? 'outlined' : 'contained'}
              component={!onSetReminder && reminderPath ? 'a' : 'button'}
              href={!onSetReminder && reminderPath ? reminderPath : undefined}
              startIcon={<NotificationsNoneRoundedIcon />}
              aria-label={`${reminderState?.configured ? 'Edit' : 'Set'} reminder for ${resolvedMedication.name || 'medication'}`}
              onClick={onSetReminder ? () => onSetReminder(resolvedMedication) : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 40,
                borderRadius: 1.3,
                px: 1.2,
                fontSize: '0.78rem',
                fontWeight: 900,
                textTransform: 'none',
                boxShadow: 'none',
                bgcolor: reminderState?.configured
                  ? colors.semantic.surface
                  : colors.semantic.profileFlow.medicationManager.primaryAction,
                color: reminderState?.configured
                  ? colors.semantic.profileFlow.completionText
                  : undefined,
                borderColor: reminderState?.configured
                  ? colors.semantic.profileFlow.progressPartial
                  : undefined,
                '&:hover': {
                  bgcolor: reminderState?.configured
                    ? colors.semantic.profileFlow.completionBg
                    : colors.semantic.profileFlow.medicationManager.primaryActionHover,
                  boxShadow: 'none',
                },
              }}
            >
              {reminderState?.configured ? 'Edit reminder' : 'Set reminder'}
            </Button>
          ) : isNoChange ? (
            <Box sx={{ flex: 1 }} />
          ) : (
            <Button
              variant="contained"
              disabled={isPrimaryActionDisabled}
              aria-label={isCourseCompletion
                ? `${isMatched ? 'Complete' : 'Save'} ${actionMedicationName || 'medication'} course completion${isMatched ? '' : ' to Timeline'}`
                : `${isTemporaryHold ? 'Apply temporary hold for' : isUpdate ? 'Update' : 'Add'} ${actionMedicationName || 'medication'}${isTemporaryHold ? '' : ' medication plan'}`}
              onClick={handleSave}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 40,
                borderRadius: 1.3,
                px: 1.2,
                fontSize: '0.78rem',
                fontWeight: 900,
                textTransform: 'none',
                boxShadow: 'none',
                bgcolor: pageLayout ? colors.semantic.profileFlow.medicationManager.primaryAction : colors.brand.deep,
                '&.Mui-disabled': {
                  bgcolor: colors.semantic.profileFlow.notStartedBg,
                  color: alpha(colors.semantic.ink, 0.48),
                },
                '&:hover': {
                  bgcolor: pageLayout ? colors.semantic.profileFlow.medicationManager.primaryActionHover : colors.brand.ink,
                  boxShadow: 'none',
                },
              }}
            >
            {pageLayout ? pageSaveLabel : saveLabel}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={!pageLayout && (isResolved || isIgnored)}
            onClick={() => onEditFirst(candidate)}
            sx={{
              height: 40,
              borderRadius: 1.3,
              px: 1,
              minWidth: { xs: 92, sm: 104 },
              fontSize: '0.78rem',
              fontWeight: 900,
              textTransform: 'none',
              color: colors.semantic.profileFlow.completionText,
              borderColor: colors.semantic.profileFlow.progressPartial,
              '&:hover': {
                borderColor: colors.semantic.profileFlow.progressFilled,
                bgcolor: colors.semantic.profileFlow.completionBg,
              },
            }}
          >
            {pageLayout ? 'Edit' : isEditing ? 'Continue editing' : blocksDirectSave ? 'Review details' : 'Edit details'}
          </Button>
          <Button
            variant="text"
            disabled={isIgnored || (isResolved && !pageLayout)}
            onClick={() => onIgnore(candidate)}
            sx={{
              height: 40,
              borderRadius: 1.3,
              px: 0.75,
              minWidth: 68,
              fontSize: '0.78rem',
              fontWeight: 900,
              textTransform: 'none',
              color: alpha(colors.semantic.ink, 0.66),
            }}
          >
            {isIgnored ? 'Skipped' : pageLayout ? 'Skip' : isEditing ? 'Handled manually' : 'Ignore'}
          </Button>
        </Stack>
      </Box>

    </Box>
  );
};

export default MedicationPlanCandidateReviewRow;
